import { useState, type FormEvent } from "react";
import type { Counterparty } from "@deca/shared-types";
import {
  useCounterparties,
  useDeleteCounterparty,
  useMe,
  useSaveCounterparty,
  useToggleCounterparty,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, Modal, Notice, PageHeader, Select } from "../components/ui";
import { IconPlus } from "../components/icons";
import { counterpartyRoleLabel, formatDateTime } from "../lib/format";

/** Catálogo de contrapartes del tenant, con filtro por rol (CLAUDE.md Fase 4). */

const column = createColumnHelper<Counterparty>();

const ROLES = [
  "CARGADOR_CONTRACTUAL",
  "TRANSPORTISTA_EFECTIVO",
  "OPERADOR_INTERMEDIO",
  "DESTINATARIO",
] as const;

const EMPTY = {
  name: "",
  nif: "",
  address: "",
  email: "",
  phone: "",
  defaultRole: "CARGADOR_CONTRACTUAL" as (typeof ROLES)[number],
};

export function CounterpartiesPage() {
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Counterparty | null>(null);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ ...EMPTY });
  const [toDelete, setToDelete] = useState<Counterparty | null>(null);
  const [toDeactivate, setToDeactivate] = useState<Counterparty | null>(null);
  // Filtro de estado (en el navegador: el filtro `isActive` de la API no distingue "false")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const query = useCounterparties({
    ...(roleFilter ? { defaultRole: roleFilter } : {}),
    ...(search ? { search } : {}),
  });
  const save = useSaveCounterparty();
  const del = useDeleteCounterparty();
  const deactivate = useToggleCounterparty("deactivate");
  const reactivate = useToggleCounterparty("reactivate");

  const me = useMe();
  const canManage = me.data?.role === "ADMIN" || me.data?.role === "GESTOR";

  const items = (query.data?.items ?? []).filter((item) =>
    statusFilter === "all" ? true : statusFilter === "active" ? item.isActive : !item.isActive,
  );

  const openCreate = (): void => {
    setEditing(null);
    setValues({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (item: Counterparty): void => {
    setEditing(item);
    setValues({
      name: item.name,
      nif: item.nif,
      address: item.address,
      email: item.email ?? "",
      phone: item.phone ?? "",
      defaultRole: item.defaultRole,
    });
    setOpen(true);
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: Record<string, unknown> = {
      name: values.name.trim(),
      nif: values.nif.trim().toUpperCase(),
      address: values.address.trim(),
      defaultRole: values.defaultRole,
    };
    if (values.email.trim()) payload.email = values.email.trim();
    if (values.phone.trim()) payload.phone = values.phone.trim();

    save.mutate(editing ? { id: editing.id, values: payload } : { values: payload }, {
      onSuccess: () => setOpen(false),
    });
  };

  const columns = [
    column.accessor("name", { header: "Nombre" }),
    column.accessor("nif", {
      header: "NIF",
      cell: (info) => <span className="ident">{info.getValue()}</span>,
    }),
    column.accessor("defaultRole", {
      header: "Rol habitual",
      cell: (info) => counterpartyRoleLabel(info.getValue()),
    }),
    column.accessor("address", { header: "Dirección" }),
    column.accessor("createdAt", {
      header: "Alta",
      cell: (info) => <span className="num">{formatDateTime(info.getValue())}</span>,
    }),
    column.accessor("isActive", {
      header: "Estado",
      cell: (info) => (info.getValue() ? <Chip tone="ok">Activa</Chip> : <Chip tone="neutral">Inactiva</Chip>),
    }),
    column.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <Button onClick={() => openEdit(info.row.original)}>Editar</Button>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Catálogos"
        title="Contrapartes"
        description="Cargadores, transportistas, operadores intermedios y destinatarios de la empresa. Al emitir un DeCA se pueden elegir de aquí o escribir los datos en el momento; en ese caso se dan de alta solos."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <IconPlus className="h-4 w-4" />
            Nueva contraparte
          </Button>
        }
      />

      <div className="surface mb-4 grid gap-3 p-4 sm:grid-cols-3">
        <Field label="Rol" htmlFor="filtro-rol">
          <Select id="filtro-rol" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
            <option value="">Todos los roles</option>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {counterpartyRoleLabel(role)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Estado" htmlFor="filtro-estado">
          <Select
            id="filtro-estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as "all" | "active" | "inactive")}
          >
            <option value="all">Todas</option>
            <option value="active">Activas</option>
            <option value="inactive">Inactivas</option>
          </Select>
        </Field>
        <Field label="Buscar por nombre" htmlFor="filtro-buscar">
          <Input id="filtro-buscar" value={search} onChange={(event) => setSearch(event.target.value)} />
        </Field>
      </div>

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          isLoading={query.isLoading}
          emptyMessage={
            statusFilter === "all"
              ? "Todavía no hay contrapartes. Crea la primera para poder emitir documentos seleccionándolas."
              : "No hay contrapartes con ese estado."
          }
        />
      )}

      <Modal
        open={toDeactivate !== null}
        title="Dar de baja la contraparte"
        onClose={() => setToDeactivate(null)}
      >
        <div className="space-y-4">
          <Notice tone="warn">
            <strong>{toDeactivate?.name}</strong> pasará a estado <strong>Inactiva</strong> y no podrá
            seleccionarse en nuevos DeCA. Los documentos existentes no cambian. Podrás recuperarla cuando
            quieras desde Editar → Recuperar.
          </Notice>
          {deactivate.isError ? <ErrorNotice error={deactivate.error} /> : null}
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={deactivate.isPending}
              onClick={() => {
                if (!toDeactivate) return;
                deactivate.mutate(toDeactivate.id, { onSuccess: () => setToDeactivate(null) });
              }}
            >
              {deactivate.isPending ? "Procesando…" : "Dar de baja"}
            </Button>
            <Button onClick={() => setToDeactivate(null)} disabled={deactivate.isPending}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={toDelete !== null}
        title="Eliminar contraparte"
        onClose={() => setToDelete(null)}
      >
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          ¿Seguro que quieres eliminar{" "}
          <strong>{toDelete?.name}</strong>?{" "}
          Esta acción no se puede deshacer.
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Si esta contraparte aparece en algún DeCA, el borrado será bloqueado por el servidor.
        </p>

        {del.isError ? <ErrorNotice error={del.error} /> : null}

        <div className="flex gap-2">
          <Button
            variant="danger"
            disabled={del.isPending}
            onClick={() => {
              if (!toDelete) return;
              del.mutate(toDelete.id, { onSuccess: () => setToDelete(null) });
            }}
          >
            {del.isPending ? "Eliminando…" : "Sí, eliminar"}
          </Button>
          <Button onClick={() => setToDelete(null)} disabled={del.isPending}>
            Cancelar
          </Button>
        </div>
      </Modal>

      <Modal open={open} title={editing ? "Editar contraparte" : "Nueva contraparte"} onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nombre o razón social" htmlFor="cp-name">
              <Input
                id="cp-name"
                required
                value={values.name}
                onChange={(event) => setValues({ ...values, name: event.target.value })}
              />
            </Field>
            <Field label="NIF" htmlFor="cp-nif">
              <Input
                id="cp-nif"
                required
                value={values.nif}
                onChange={(event) => setValues({ ...values, nif: event.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Dirección" htmlFor="cp-address">
                <Input
                  id="cp-address"
                  required
                  value={values.address}
                  onChange={(event) => setValues({ ...values, address: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Correo (opcional)" htmlFor="cp-email">
              <Input
                id="cp-email"
                type="email"
                value={values.email}
                onChange={(event) => setValues({ ...values, email: event.target.value })}
              />
            </Field>
            <Field label="Teléfono (opcional)" htmlFor="cp-phone">
              <Input
                id="cp-phone"
                value={values.phone}
                onChange={(event) => setValues({ ...values, phone: event.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field
                label="Rol habitual"
                htmlFor="cp-role"
                hint="Es un valor por defecto para preseleccionar en el formulario, no una restricción: la misma contraparte puede intervenir con otro papel."
              >
                <Select
                  id="cp-role"
                  value={values.defaultRole}
                  onChange={(event) =>
                    setValues({ ...values, defaultRole: event.target.value as (typeof ROLES)[number] })
                  }
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {counterpartyRoleLabel(role)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>

          {save.isError ? <ErrorNotice error={save.error} /> : null}

          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </Button>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
          </div>

          {canManage && editing ? (
            <>
              <hr className="border-ink-100" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-ink-500">Acciones de administrador</p>
                <div className="flex gap-2">
                  {editing.isActive ? (
                    <Button
                      variant="quiet"
                      onClick={() => {
                        deactivate.reset();
                        setOpen(false);
                        setToDeactivate(editing);
                      }}
                    >
                      Dar de baja
                    </Button>
                  ) : (
                    <Button
                      variant="quiet"
                      disabled={reactivate.isPending}
                      onClick={() =>
                        reactivate.mutate(editing.id, { onSuccess: () => setOpen(false) })
                      }
                    >
                      {reactivate.isPending ? "Recuperando…" : "Recuperar"}
                    </Button>
                  )}
                  <Button
                    variant="quiet"
                    onClick={() => {
                      del.reset();
                      setOpen(false);
                      setToDelete(editing);
                    }}
                  >
                    Eliminar
                  </Button>
                </div>
                {reactivate.isError ? <ErrorNotice error={reactivate.error} /> : null}
              </div>
            </>
          ) : null}
        </form>
      </Modal>
    </>
  );
}
